import { db } from "../lib/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

export type CashbackTier = { threshold: number; amount: number };
export type CashbackSettings = {
  enabled: boolean;
  tiers: CashbackTier[]; // sorted by threshold ascending
  message?: string;
  updatedAt?: number;
};

const DEFAULT_SETTINGS: CashbackSettings = {
  enabled: true,
  tiers: [
    { threshold: 5000, amount: 100 },
    { threshold: 10000, amount: 150 },
    { threshold: 50000, amount: 200 },
    { threshold: 100000, amount: 300 },
  ],
  message: "Limited-time buyer cashback: ₦100 under ₦5k, ₦150 under ₦10k, ₦200 under ₦50k, ₦300 under ₦100k.",
  updatedAt: Date.now(),
};

export async function getCashbackSettings(): Promise<CashbackSettings> {
  const ref = doc(db, "settings", "cashback");
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data() as CashbackSettings;
    const tiers = [...(data.tiers || [])].sort((a, b) => a.threshold - b.threshold);
    return { enabled: !!data.enabled, tiers, message: data.message, updatedAt: data.updatedAt };
  }
  return DEFAULT_SETTINGS;
}

export async function updateCashbackSettings(settings: CashbackSettings): Promise<void> {
  const ref = doc(db, "settings", "cashback");
  const sorted = { ...settings, tiers: [...settings.tiers].sort((a, b) => a.threshold - b.threshold), updatedAt: Date.now() };
  await setDoc(ref, sorted, { merge: true });
}

export function calculateCashbackForAmount(total: number, settings: CashbackSettings): number {
  if (!settings.enabled || !settings.tiers?.length || !isFinite(total) || total <= 0) return 0;
  // Find highest tier with threshold >= total or last tier below total
  let cashback = 0;
  for (const tier of settings.tiers) {
    if (total <= tier.threshold) {
      cashback = tier.amount; 
      break;
    }
    cashback = tier.amount; // if total is above all thresholds, use last tier amount
  }
  return cashback;
}

// Featured Listings admin control
export type FeaturedListingsSettings = {
  listingIds: string[];
  updatedAt?: number;
};

export async function getFeaturedListingIds(): Promise<string[]> {
  const ref = doc(db, "settings", "featured_listings");
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data() as FeaturedListingsSettings;
    const ids = Array.isArray(data.listingIds) ? data.listingIds.filter((v) => typeof v === 'string') : [];
    return ids;
  }
  return [];
}

export async function updateFeaturedListingIds(ids: string[]): Promise<void> {
  const ref = doc(db, "settings", "featured_listings");
  const payload: FeaturedListingsSettings = { listingIds: ids.slice(0, 20), updatedAt: Date.now() };
  await setDoc(ref, payload, { merge: true });
}

// Platform fee rate (e.g. 0.10 = 10%)
export async function getPlatformFeeRate(): Promise<number> {
  try {
    const ref = doc(db, "settings", "platform");
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data() as any;
      if (typeof data.feeRate === 'number') return data.feeRate;
    }
  } catch {}
  return 0.10; // default 10%
}

export async function updatePlatformFeeRate(rate: number): Promise<void> {
  const ref = doc(db, "settings", "platform");
  await setDoc(ref, { feeRate: rate, updatedAt: Date.now() }, { merge: true });
}

// Site-wide, admin-editable content: social links + tutorial videos.
export type SocialLinks = {
  instagram?: string;
  twitter?: string;
  facebook?: string;
  tiktok?: string;
  youtube?: string;
  whatsapp?: string;
  linkedin?: string;
};

export type TutorialItem = {
  id: string;
  title: string;
  description?: string;
  videoUrl?: string;
};

export type SiteSettings = {
  social?: SocialLinks;
  tutorials?: {
    sellerVideoUrl?: string;
    buyerVideoUrl?: string;
    items?: TutorialItem[];
  };
  updatedAt?: number;
};

export async function getSiteSettings(): Promise<SiteSettings> {
  try {
    const ref = doc(db, "settings", "site");
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data() as SiteSettings;
  } catch {}
  return {};
}

export async function updateSiteSettings(partial: Partial<SiteSettings>): Promise<void> {
  const ref = doc(db, "settings", "site");
  await setDoc(ref, { ...partial, updatedAt: Date.now() }, { merge: true });
}